import {Fragment, ReactNode} from "react";
import {ErrorCode, FileError, FileRejection} from "react-dropzone";
import {DisclosurePageRef, DisclosurePageState, PageLocked} from "platformed-client/types";
import {errorDescription, HTTPError} from "platformed-client";
import NoTranslate from "platformed-components/i18n/NoTranslate";
import T from "platformed-components/i18n/T";
import {formatFileSize} from "../../../../../components/fileUploads";
import {toaster} from "../../../../../components/ui/toaster";
import {LARGE_FILE_UPLOAD_LIMIT_BYTES} from "../../../../../hooks/fileUpload";

export type DraftsWrite = "upload" | "add" | "remove" | "commit";

const TITLES: Record<DraftsWrite, ReactNode> = {
  upload: <T keyName="c.Could_not_upload_the_draft" />,
  add: <T keyName="c.Could_not_add_documents" />,
  remove: <T keyName="c.Could_not_remove_the_document" />,
  commit: <T keyName="c.Could_not_commit_the_draft" />,
};

const status = (error: unknown) => (error instanceof HTTPError ? error.response.status : undefined);

/** A commit can also be refused by a checklist's lock. */
export const lockedPage = (error: unknown): PageLocked | undefined =>
  error instanceof HTTPError && error.response.status === 409 && error.json?.sub_type === "page_locked"
    ? (error.json.extra as PageLocked)
    : undefined;

/** A 409 that is not a lock means what the write targeted changed since it was read. */
export const isStaleWrite = (error: unknown) => status(error) === 409 && lockedPage(error) == null;

const samePage = (a: DisclosurePageRef, b: DisclosurePageRef) =>
  a.type === b.type && (a.type !== "Checklist" || b.type !== "Checklist" || a.content === b.content);

/** `pages` names a locked page as the sidebar does. */
export const lockedPageMessage = (error: unknown, pages: DisclosurePageState[]): ReactNode | undefined => {
  const locked = lockedPage(error);
  if (locked == null) return undefined;
  const name = pages.find(page => samePage(page.page, locked.page))?.name ?? locked.name;
  return (
    <T
      keyName="tprm.disclosures.drafts.error.page_locked[parameterized]"
      params={{name: <NoTranslate>{name}</NoTranslate>}}
    />
  );
};

/** Every write refetches the documents and lock state, so the page already shows why a write was refused. */
export const reportWriteError = (write: DraftsWrite, error: unknown, pages: DisclosurePageState[]) => {
  const description =
    lockedPageMessage(error, pages) ??
    (write === "commit" && isStaleWrite(error) ? (
      <T keyName="tprm.disclosures.drafts.error.commit_conflict" />
    ) : (
      errorDescription(error)
    ));
  toaster.error({title: TITLES[write], description});
};

export const reportUploadBusy = (files: File[]) => {
  for (const file of files) {
    toaster.error({title: file.name, description: <T keyName="tprm.disclosures.drafts.error.upload_busy" />});
  }
};

export const reportDuplicates = (files: File[]) => {
  for (const file of files) {
    toaster.error({title: file.name, description: <T keyName="tprm.disclosures.drafts.duplicate_name" />});
  }
};

const rejectionReason = (error: FileError, maxFiles: number | undefined): ReactNode => {
  switch (error.code) {
    case ErrorCode.FileInvalidType:
      return <T keyName="c.Unsupported_file_type" />;
    case ErrorCode.FileTooLarge:
      return (
        <T
          keyName="c.File_too_large_max[parameterized]"
          params={{size: formatFileSize(LARGE_FILE_UPLOAD_LIMIT_BYTES)}}
        />
      );
    case ErrorCode.TooManyFiles:
      return <T keyName="c.Too_many_files_max[parameterized]" params={{count: maxFiles ?? 1}} />;
    default:
      return <NoTranslate>{error.message}</NoTranslate>;
  }
};

/** `maxFiles` is the zone's own limit. */
export const reportRejected = (rejections: FileRejection[], maxFiles?: number) => {
  for (const {file, errors} of rejections) {
    toaster.error({
      title: file.name,
      description: (
        <>
          {errors.map((error, index) => (
            <Fragment key={index}>
              {index > 0 && " "}
              {rejectionReason(error, maxFiles)}
            </Fragment>
          ))}
        </>
      ),
    });
  }
};
